<script setup lang="ts">
//#region import
import { computed, ref } from 'vue';
import { pick } from 'lodash-es';

import { useI18n } from 'vue-i18n';
import UploadFile from '@/components/UploadFile.vue';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import { type UploadedFile, type Asset, type EmitsToListeners, typeGuard, type ErrorResponse, type ToErrorMap } from '@/types/core';
import type {
  PutControlTowerMediaBuyingPaymentMethodsByIdData,
} from '@/types/openapi-ts';
//#endregion

//#region types
interface Props {
  id?: string | null
}

interface  Emits {
  'close': [],
  'success': [],
};

export interface AddPaymentMethodMediaBuyingParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}

interface LimitValues {
  min: number | null
  max: number | null
}

type PaymentMethodsFormErrors = ToErrorMap<{
  files: any;
  // не уверен что currency_codes существует
  currency_codes: string[];
} & PutControlTowerMediaBuyingPaymentMethodsByIdData['body']>;
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  id: null,
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const { t } = useI18n();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref<PaymentMethodsFormErrors>({} as PaymentMethodsFormErrors);
const data = ref<PutControlTowerMediaBuyingPaymentMethodsByIdData['body']>({
  name: '',
  hidden: false,
  allowed_currencies: 'all',
  currencies: [],
  wallet_regexp: null,
  wallet_placeholder: null,
  logo_base64: null,
  limits: [{
    currency_code: '',
    is_default: true,
    max_amount: null,
    min_amount: null,
  }],
});
const files = ref<UploadedFile[]>([]);
//#endregion

//#region computed
const currencies = computed(() => store.getters['misc/currencies']);
const status = computed({
  get() {
    return !data.value.hidden;
  },
  set(newValue) {
    data.value.hidden = !newValue;
  },
});

const filteredCurrencyOptions = computed(() => {
  return currencies.value.filter((el) =>
    ![...data.value.limits.map(({ currency_code }) => currency_code)].includes(el.code));
});

const defaultLimitIndex = computed(() => {
  const index = data.value.limits.findIndex((el) => el.is_default);
  return index >= 0 ? index : 0;
});

const defaultLimit = computed(() => data.value.limits[defaultLimitIndex.value]!);

const listForOtherLimits = computed(() => {
  return data.value.limits
    .map((limit, i) => ({ limit, i }))
    .filter(({ i }) => i !== defaultLimitIndex.value);
});

const allowedCurrenciesOptions = computed(() => {
  return [
    {
      label: t('settings.paymentMethods.popup.all'),
      value: 'all',
    },
    {
      label: t('settings.paymentMethods.popup.selected'),
      value: 'selected',
    },
  ];
});
//#endregion

//#region methods
const open = async () => {
  isVisible.value = true;

  if (!props.id) return;

  isLoading.value = true;

  try {
    const { data: { payload } } = await api.getControlMediaBuyingPaymentMethodById(props.id);

    data.value = {
      ...(pick(payload, [
        'name',
        'currencies',
        'wallet_placeholder',
        'wallet_regexp',
        'logo_base64',
        'hidden',
        'allowed_currencies',
        'limits',
      ]) as PutControlTowerMediaBuyingPaymentMethodsByIdData['body']),
    };

    files.value = data.value.logo_base64 ? [{
      uid: 1,
      name: 'File uploaded',
      file: data.value.logo_base64,
    }] : [];
  } finally {
    isLoading.value = false;
  }
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
  errors.value = {} as PaymentMethodsFormErrors;
};

const handleSave = () => {
  const promise = props.id
    ? api.putControlMediaBuyingPaymentMethods(props.id, data.value)
    : api.postControlMediaBuyingPaymentMethods(data.value);

  promise
    .then(() => {
      emit('success');
      handleClose();
    })
    .catch((e) => {
      if(typeGuard<ErrorResponse<PaymentMethodsFormErrors>>(e)){
        errors.value = e?.data?.errors || {};
      }
    });
};

const getBase64 = (file: Blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = (error) => reject(error);
});

const uploadFile = (asset: Asset): Promise<void> => {
  if (!asset) return Promise.reject(new Error('File not found'));
  errors.value.files = undefined;
  if (asset.size > 100000) { // 100kb
    errors.value.files = [{ message: t('settings.paymentMethods.popup.limitFileSize') }];
    files.value = [];
    return Promise.reject(new Error('File size exceeds 100 KB'));
  }

  return getBase64(asset).then((res: any) => {
    files.value = [{
      uid: asset.uid,
      name: asset.name,
      file: res,
    }];
    data.value.logo_base64 = res;
  });
};

const removeFile = () => {
  data.value.logo_base64 = '';
  files.value = [];
};

const getCurrencyByCode = (code: string) => {
  return currencies.value?.find((el) => el.code === code);
};

const addCurrency = () => {
  data.value.limits.push({
    currency_code: '',
    max_amount: null,
    min_amount: null,
    is_default: false,
  });
};

const getRangeError = (id: number) => {
  const min = errors.value.limits?.[id]?.min_amount;
  const max = errors.value.limits?.[id]?.max_amount;
  return {
    min,
    max,
  };
};

const removeCurrency = (i: number) => {
  data.value.limits = [...data.value.limits.slice(0, i), ...data.value.limits.slice(i + 1)];
  delete errors.value.limits;
};

//#endregion
defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="add-payment-method-popup"
    :title="id ? $t('settings.paymentMethods.popup.titleEdit') : $t('settings.paymentMethods.popup.title')"
    :width="500"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiInput
          v-model="data.name"
          :label="$t('settings.paymentMethods.popup.name')"
          autosize
          :placeholder="$t('settings.paymentMethods.popup.placeholderName')"
          type="text"
          :error="errors.name"
          size="big"
        />
        <CardTitle
          :title="$t('settings.paymentMethods.popup.status')"
        >
          <UiSwitch
            v-model="status"
            :status-text="$t(`settings.paymentMethods.statuses.${data.hidden ? 'disabled' : 'active'}`)"
            :status-type="data.hidden ? 'negative' : 'positive'"
          />
        </CardTitle>
        <UiSeparator />
        <UiRadioGroup
          v-model="data.allowed_currencies"
          :label="$t('settings.paymentMethods.popup.currencies')"
          :options="allowedCurrenciesOptions"
        >
          <UiSelectElement
            v-model="data.currencies"
            :placeholder="$t('settings.paymentMethods.popup.placeholderCurrencies')"
            :list="currencies"
            :disabled="data.allowed_currencies === 'all'"
            search-param="name"
            filterable
            :max-collapse-tags="5"
            collapse-tag-tooltip
            multiple
            option-value-param="code"
            option-label-param="code"
            :error="errors.currency_codes"
            size="big"
          />
        </UiRadioGroup>
        <UiInput
          v-model="data.wallet_regexp"
          :label="$t('settings.paymentMethods.popup.walletRegexp')"
          autosize
          size="big"
          :placeholder="$t('settings.paymentMethods.popup.walletRegexp')"
          type="text"
          :error="errors.wallet_regexp"
        />
        <UiInput
          v-model="data.wallet_placeholder"
          :label="$t('settings.paymentMethods.popup.walletPlaceholder')"
          autosize
          size="big"
          :placeholder="$t('settings.paymentMethods.popup.walletPlaceholder')"
          type="text"
          :error="errors.wallet_placeholder"
        />
        <UploadFile
          v-model="files"
          :title="$t('settings.paymentMethods.popup.logo')"
          accept=".png, .jpg"
          :limit-file="1"
          is-dragger
          :upload-files-action="uploadFile"
          :remove-file="removeFile"
          :error="errors.files || errors.logo_base64"
        >
          <template v-slot:upload-text>
            <span>{{ $t('settings.paymentMethods.popup.dropFileHereOr') }} <em>{{ $t('settings.paymentMethods.popup.selectFile') }}</em></span>
          </template>
        </UploadFile>
        <UiSeparator />
        <UiLabel
          :font-size="16"
          bold
          :label="$t('settings.mediaBuyingPaymentMethods.limits')"
        />
        <FormRow
          :title="$t('settings.mediaBuyingPaymentMethods.default')"
          class="t:space-y-8"
        >
          <div class="t:flex t:space-x-8">
            <UiRangeInput
              :value="{min: defaultLimit.min_amount, max: defaultLimit.max_amount}"
              class="card-filter__range-input"
              error-tooltip
              type="number"
              :error="getRangeError(defaultLimitIndex)"
              @change="(limits: LimitValues) => {
                data.limits[defaultLimitIndex]!.min_amount = limits.min;
                data.limits[defaultLimitIndex]!.max_amount = limits.max;
              }"
            />
            <UiSelectElement
              v-model="data.limits[defaultLimitIndex]!.currency_code"
              :placeholder="$t('settings.paymentMethods.popup.placeholderCurrencies')"
              :list="[
                ...(defaultLimit.currency_code ? [getCurrencyByCode(defaultLimit.currency_code)] : []),
                ...filteredCurrencyOptions
              ]"
              :error="errors.limits?.[defaultLimitIndex]?.currency_code"
              option-value-param="code"
              filterable
            />
          </div>
        </FormRow>
        <FormRow
          :title="$t('settings.mediaBuyingPaymentMethods.otherCurrencies')"
          class="t:space-y-8"
        >
          <template v-slot:action>
            <UiButton
              icon="plus"
              no-border
              @click="addCurrency"
            >
              {{ $t('settings.mediaBuyingPaymentMethods.addCurrency') }}
            </UiButton>
          </template>
          <div
            v-for="{ limit, i } in listForOtherLimits"
            :key="i"
            class="t:flex t:items-start t:space-x-8"
          >
            <UiRangeInput
              :value="{min: limit.min_amount, max: limit.max_amount}"
              type="number"
              error-tooltip
              :error="getRangeError(i)"
              @change="(limits: LimitValues) => {
                data.limits[i]!.min_amount = limits.min;
                data.limits[i]!.max_amount = limits.max;
              }"
            />
            <UiSelectElement
              v-model="data.limits[i]!.currency_code"
              :placeholder="$t('settings.paymentMethods.popup.placeholderCurrencies')"
              :list="[
                ...(limit.currency_code ? [getCurrencyByCode(limit.currency_code)] : []),
                ...filteredCurrencyOptions
              ]"
              :error="errors.limits?.[i]?.currency_code"
              option-value-param="code"
              filterable
            />
            <UiIcon
              size="14px"
              name="times"
              color="var(--text-negative)"
              class="t:mt-8 t:cursor-pointer"
              @click="removeCurrency(i)"
            />
          </div>
        </FormRow>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.add-payment-method-media-buying-popup {

}
</style>
